<script setup lang="ts">
import { sm as isMobile } from "~/utils/breakpoints"

import { StaticPaginatorProps } from "./StaticPaginator.props"

const props = withDefaults(defineProps<StaticPaginatorProps>(), {
  page: 1,
  totalPages: 1,
  hideLabels: false
})

const emit =
  defineEmits<{
    (e: "pageChange", page: number): void
  }>()

const isSecondPageVisible = computed(() => !(props.page > 3))
const isSecondToLastVisible = computed(
  () => !(props.page < props.totalPages - 2)
)

const range = computed<Array<number>>(() => {
  if (arePagesLessEqualThanSix.value) {
    return Array.from({ length: props.totalPages }, (_, index) => index + 1)
  }
  const MAX_LENGTH = isMobile.value ? 1 : 3
  if (isSecondPageVisible.value) {
    return Array.from({ length: MAX_LENGTH }, (_, index) => index - 1 + 4)
  }
  if (isSecondToLastVisible.value) {
    return Array.from(
      { length: MAX_LENGTH },
      (_, index) => props.totalPages - (isMobile.value ? 2 : 4) + index
    )
  }
  return Array.from(
    { length: MAX_LENGTH },
    (_, index) => props.page + (index - (isMobile.value ? 0 : 1))
  )
})

const arePagesLessEqualThanSix = computed(() => props.totalPages <= 5)

const handleChangePage = (selectedPage: number) => {
  emit("pageChange", selectedPage)
}
</script>

<template>
  <div
    class="paginator border-slate-20 flex h-[56px] rounded-[4px] border py-2"
  >
    <button
      aria-label="Navigate previous"
      class="
        paginator__previous-button
        flex
        items-center
        justify-center
        gap-1
        px-4
      "
      :class="[{ 'paginator-button': page !== 1 }]"
      :disabled="page === 1"
      @click="handleChangePage(page - 1)"
    >
      <IconsArrowPaginatorLeft
        class="h-6 w-6"
        :class="[page === 1 ? 'text-slate-40' : 'text-green-main']"
      />
      <p
        v-if="!hideLabels"
        class="pig hidden font-medium md:flex"
        :class="[page === 1 ? 'text-slate-40' : 'text-green-main']"
      >
        {{ $t("navigation.paginator.previous") }}
      </p>
    </button>
    <div
      class="
        paginator__numeric-pages
        border-slate-20
        flex
        border-l border-r
        px-2
      "
      v-if="arePagesLessEqualThanSix"
    >
      <button
        @click="handleChangePage(item)"
        class="numeric-page-button"
        :class="{ selected: page === item, 'paginator-button': page !== item }"
        v-for="item in range"
        :disabled="page === item"
      >
        {{ item }}
      </button>
    </div>
    <div
      class="
        paginator__numeric-pages
        border-slate-20
        flex
        border-l border-r
        px-2
      "
      v-else
    >
      <button
        class="numeric-page-button"
        :class="{ selected: page === 1, 'paginator-button': page !== 1 }"
        @click="handleChangePage(1)"
        :disabled="page === 1"
      >
        1
      </button>
      <button
        @click="handleChangePage(2)"
        :disabled="page === 2"
        class="numeric-page-button"
        :class="{
          selected: page === 2,
          'paginator-button': page !== 2,
          'disabled text-slate-40': !isSecondPageVisible
        }"
      >
        {{ isSecondPageVisible ? 2 : "..." }}
      </button>
      <button
        @click="handleChangePage(item)"
        :disabled="page === item"
        class="numeric-page-button"
        :class="{ selected: page === item, 'paginator-button': page !== item }"
        v-for="item in range"
      >
        {{ item }}
      </button>
      <button
        @click="handleChangePage(props.totalPages - 1)"
        :disabled="page === props.totalPages - 1"
        class="numeric-page-button"
        :class="{
          selected: page === props.totalPages - 1,
          'paginator-button': page !== props.totalPages - 1,
          'disabled text-slate-40': !isSecondToLastVisible
        }"
      >
        {{ isSecondToLastVisible ? props.totalPages - 1 : "..." }}
      </button>
      <button
        @click="handleChangePage(props.totalPages)"
        :disabled="page === props.totalPages"
        class="numeric-page-button"
        :class="{
          selected: page === props.totalPages,
          'paginator-button': page !== props.totalPages
        }"
      >
        {{ props.totalPages }}
      </button>
    </div>
    <button
      aria-label="Navigate next"
      class="paginator__next-button flex items-center justify-center gap-1 px-4"
      @click="handleChangePage(page + 1)"
      :disabled="page === props.totalPages"
      :class="[
        {
          'paginator-button': page !== props.totalPages
        }
      ]"
    >
      <p
        v-if="!hideLabels"
        class="pig hidden font-medium md:flex"
        :class="[
          page === props.totalPages ? 'text-slate-40' : 'text-green-main'
        ]"
      >
        {{ $t("navigation.paginator.next") }}
      </p>
      <IconsArrowPaginatorRight
        class="h-6 w-6"
        :class="[
          page === props.totalPages ? 'text-slate-40' : 'text-green-main'
        ]"
      />
    </button>
  </div>
</template>

<style scoped lang="scss">

.numeric-page-button {
  @apply flex h-10 w-10 items-center justify-center rounded-[4px];
  &.selected {
    @apply bg-green-10 text-green-70 font-bold;
  }
}
.paginator-button {
  @apply pointer-events-auto cursor-pointer;
}

</style>
